/* =========================================================================
   ClueVine — Cipher (hidden-word deduction)
   Guess the secret word; each committed guess returns per-letter clues
   (correct / present / absent), with proper duplicate-letter handling.
   ========================================================================= */

import { pickAvoiding } from '../../engine/seed';
import type {
  DifficultyLevel,
  GenerateContext,
  HintOutcome,
  InputKey,
  ModeScore,
  PuzzleStatus,
  ResultSummary,
} from '../../engine/types';

export type Clue = 'correct' | 'present' | 'absent';

export interface CipherConfig {
  length: number;
  maxGuesses: number;
}

export interface CipherState {
  answer: string;
  length: number;
  maxGuesses: number;
  guesses: string[];
  current: string;
  /** Positions revealed by a hint. */
  revealed: number[];
}

export type CipherMove =
  | { type: 'add'; ch: string }
  | { type: 'remove' }
  | { type: 'submit' };

export const CIPHER_DIFFICULTIES: DifficultyLevel<CipherConfig>[] = [
  { id: 'easy', label: 'Sprout', blurb: '4-letter word', scoreMultiplier: 0.9, config: { length: 4, maxGuesses: 6 } },
  { id: 'standard', label: 'Garden', blurb: '5-letter word', scoreMultiplier: 1, config: { length: 5, maxGuesses: 6 } },
  { id: 'hard', label: 'Thicket', blurb: '6-letter word', scoreMultiplier: 1.35, config: { length: 6, maxGuesses: 6 } },
];

/** Evaluate a guess against the answer, honoring duplicate letters (two passes). */
export function evaluateGuess(guess: string, answer: string): Clue[] {
  const result: Clue[] = new Array(guess.length).fill('absent');
  const counts: Record<string, number> = {};
  for (const ch of answer) counts[ch] = (counts[ch] ?? 0) + 1;

  for (let i = 0; i < guess.length; i++) {
    if (guess[i] === answer[i]) {
      result[i] = 'correct';
      counts[guess[i]] -= 1;
    }
  }
  for (let i = 0; i < guess.length; i++) {
    if (result[i] === 'correct') continue;
    const c = guess[i];
    if ((counts[c] ?? 0) > 0) {
      result[i] = 'present';
      counts[c] -= 1;
    }
  }
  return result;
}

const RANK: Record<Clue, number> = { absent: 0, present: 1, correct: 2 };

/** Best-known clue state per letter, for tinting the keyboard. */
export function aggregateLetterStates(guesses: string[], answer: string): Record<string, Clue> {
  const map: Record<string, Clue> = {};
  for (const g of guesses) {
    const ev = evaluateGuess(g, answer);
    for (let i = 0; i < g.length; i++) {
      const c = g[i];
      if (!map[c] || RANK[ev[i]] > RANK[map[c]]) map[c] = ev[i];
    }
  }
  return map;
}

export function generate(ctx: GenerateContext<CipherConfig>): CipherState {
  const { length, maxGuesses } = ctx.difficulty.config;
  const pool = ctx.dictionary.answersByLength(length);
  const answer = pickAvoiding(ctx.prng, pool, ctx.avoid);
  return { answer, length, maxGuesses, guesses: [], current: '', revealed: [] };
}

export function status(state: CipherState): PuzzleStatus {
  const last = state.guesses[state.guesses.length - 1];
  if (last === state.answer) return 'won';
  if (state.guesses.length >= state.maxGuesses) return 'lost';
  return 'playing';
}

export function validateMove(
  state: CipherState,
  move: CipherMove,
): { ok: boolean; reason?: string } {
  if (move.type !== 'submit') return { ok: true };
  if (state.current.length < state.length) return { ok: false, reason: 'Not enough letters' };
  if (!/^[a-z]+$/.test(state.current)) return { ok: false, reason: 'Letters only' };
  if (state.guesses.includes(state.current)) return { ok: false, reason: 'Already guessed' };
  // dictionary check happens in the mode wrapper (it has the dictionary)
  return { ok: true };
}

export function reduce(state: CipherState, move: CipherMove): CipherState {
  switch (move.type) {
    case 'add':
      if (state.current.length >= state.length) return state;
      return { ...state, current: state.current + move.ch };
    case 'remove':
      if (state.current.length === 0) return state;
      return { ...state, current: state.current.slice(0, -1) };
    case 'submit':
      if (state.current.length < state.length) return state;
      return { ...state, guesses: [...state.guesses, state.current], current: '' };
    default:
      return state;
  }
}

export function handleKey(state: CipherState, key: InputKey): CipherMove | null {
  if (key.type === 'char') {
    return state.current.length < state.length ? { type: 'add', ch: key.value } : null;
  }
  if (key.type === 'backspace') {
    return state.current.length > 0 ? { type: 'remove' } : null;
  }
  return { type: 'submit' };
}

export function hint(state: CipherState): HintOutcome<CipherState> | null {
  if (status(state) !== 'playing') return null;

  const known = new Set<number>(state.revealed);
  for (const g of state.guesses) {
    const ev = evaluateGuess(g, state.answer);
    ev.forEach((clue, i) => {
      if (clue === 'correct') known.add(i);
    });
  }
  const unknown: number[] = [];
  for (let i = 0; i < state.length; i++) if (!known.has(i)) unknown.push(i);
  if (unknown.length === 0) return null;

  const pos = unknown[0];
  return {
    message: `Position ${pos + 1} is “${state.answer[pos].toUpperCase()}”.`,
    penalty: 60,
    state: { ...state, revealed: [...state.revealed, pos] },
  };
}

export function score(state: CipherState): ModeScore {
  const won = status(state) === 'won';
  const used = state.guesses.length;
  const parMs = state.length * 9000;

  if (!won) {
    return { base: 0, lines: [{ label: 'Not solved', value: 0, kind: 'base' }], parMs };
  }
  const base = 200 + (state.maxGuesses - used) * 150 + state.length * 40;
  return {
    base,
    lines: [{ label: `Solved in ${used}/${state.maxGuesses}`, value: base, kind: 'base' }],
    parMs,
    perfect: used === 1,
  };
}

const SQUARE: Record<Clue, string> = { correct: '🟩', present: '🟨', absent: '⬛' };

export function summarize(state: CipherState): ResultSummary {
  const won = status(state) === 'won';
  return {
    status: won ? 'won' : 'lost',
    headline: won ? `Solved in ${state.guesses.length}!` : 'Out of guesses',
    answer: state.answer.toUpperCase(),
    attempts: state.guesses.length,
    shareGrid: state.guesses.map((g) => evaluateGuess(g, state.answer).map((c) => SQUARE[c]).join('')),
  };
}

export function serialize(state: CipherState): unknown {
  return state;
}

export function deserialize(raw: unknown): CipherState | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<CipherState>;
  if (typeof s.answer !== 'string' || !Array.isArray(s.guesses) || typeof s.current !== 'string') {
    return null;
  }
  if (typeof s.length !== 'number' || typeof s.maxGuesses !== 'number') return null;
  return {
    answer: s.answer,
    length: s.length,
    maxGuesses: s.maxGuesses,
    guesses: s.guesses.filter((g): g is string => typeof g === 'string'),
    current: s.current,
    revealed: Array.isArray(s.revealed) ? s.revealed.filter((n): n is number => typeof n === 'number') : [],
  };
}
